import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon, ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline'
import api from '../../services/api'

interface Txn {
  id: number
  payment_id: string | null
  utr: string | null
  amount: number
  currency: string
  status: string
  payment_method: string | null
  user_name: string | null
  user_email: string | null
  created_at: string | null
}

const STATUS_META: Record<string, { cls: string; label: string }> = {
  captured: { cls: 'bg-green-500/10 text-green-400 border-green-500/20', label: 'Success' },
  failed:   { cls: 'bg-red-500/10 text-red-400 border-red-500/20', label: 'Failed' },
  refunded: { cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20', label: 'Refunded' },
}

const PAGE_SIZE = 20

export default function TransactionsPage() {
  const [txns, setTxns] = useState<Txn[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)

  const buildParams = () => {
    const params: Record<string, any> = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }
    if (search) params.search = search
    if (statusFilter) params.status = statusFilter
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    return params
  }

  const fetchTxns = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/transactions', { params: buildParams() })
      setTxns(res.data.transactions)
      setTotal(res.data.total)
    } catch {}
    finally { setLoading(false) }
  }

  useEffect(() => { fetchTxns() }, [search, statusFilter, dateFrom, dateTo, page])
  useEffect(() => { setPage(1) }, [search, statusFilter, dateFrom, dateTo])

  const handleExport = async () => {
    setExporting(true)
    try {
      const { skip, limit, ...filters } = buildParams()
      const res = await api.get('/admin/transactions/export', { params: filters, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'transactions.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {}
    finally { setExporting(false) }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Transactions</nav>
          <h1 className="text-2xl font-bold text-white">Transactions</h1>
          <p className="text-sm text-gray-400 mt-0.5">Money ledger — payment outcomes only (success, failed &amp; refunded). No plan or order details.</p>
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-primary-600/40 bg-primary-500/10 text-primary-300 text-sm hover:bg-primary-500/20 transition-colors disabled:opacity-50">
          <ArrowDownTrayIcon style={{ width: 15, height: 15 }} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ width: 16, height: 16 }} />
          <input
            type="text"
            placeholder="Search user, UTR, transaction ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">All Outcomes</option>
          <option value="captured">Success</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500" />
        <span className="text-xs text-gray-500">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500" />
      </div>

      {/* Table */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700 bg-dark-900 text-left text-xs text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Method</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">UTR / Txn Ref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">Loading…</td></tr>
              ) : txns.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">No transactions found</td></tr>
              ) : txns.map(t => {
                const meta = STATUS_META[t.status] || { cls: 'bg-dark-700 text-gray-400 border-dark-600', label: t.status }
                return (
                  <tr key={t.id} className="hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-100">{t.user_name || '—'}</p>
                      <p className="text-xs text-gray-500">{t.user_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-100 font-medium">₹{t.amount.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-xs text-gray-300 uppercase">{t.payment_method || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">{t.utr || t.payment_id || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {!loading && txns.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-dark-700">
            <p className="text-xs text-gray-400">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
                <ChevronLeftIcon style={{ width: 15, height: 15 }} />
              </button>
              <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
                <ChevronRightIcon style={{ width: 15, height: 15 }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
